import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  cityConfigSchema,
  CityConfigFormValues,
  RIGHTS_PROVIDER_ARRAY_FIELDS,
  TICKET_PROVIDER_ARRAY_FIELDS,
} from "../parking-payment.const";
import { TicketProviderSection } from "./city-config/ticket-provider-section";
import { RightsProviderSection } from "./city-config/rights-provider-section";
import { TicketProvider, RightsProvider, TicketProviderEnum } from "../payment-zone-area.type";
import { useGetCityConfig, useUpdateCityConfig } from "../hooks/use-city-config";
import { showNotification } from "@/components/notification";
import { formatApiError } from "@/services/web/api-error";
import { SectionDivider } from "./section-divider";

type CityConfigModalProps = {
  cityId: string;
  onClose: () => void;
};

// Backfill required array fields to [] so a config saved before these fields
// existed doesn't submit them as missing keys (the backend requires them present).
const withArrayDefaults = (provider: object | undefined, arrayFields: readonly string[]) => {
  if (!provider) return {};
  const filled: Record<string, unknown> = { ...provider };
  for (const field of arrayFields) {
    if (filled[field] === undefined) filled[field] = [];
  }
  return filled;
};

// The eTicket password is redacted on read, so an empty field means "keep the
// stored secret" — drop it from the payload rather than overwriting it with "".
// A cleared section is sent as null so the backend removes the stored provider.
const buildTicketPayload = (ticketProvider: CityConfigFormValues["ticketProvider"]): TicketProvider | null => {
  if (!ticketProvider?.provider) return null;
  const { extensoETicketPassword, ...rest } = ticketProvider;
  const payload = {
    ...rest,
    polyconseilValidityAreas: rest.polyconseilValidityAreas ?? [],
  } as TicketProvider & { passwordAlreadySet?: boolean };
  delete payload.passwordAlreadySet;
  if (extensoETicketPassword?.trim()) {
    payload.extensoETicketPassword = extensoETicketPassword;
  }
  return payload;
};

const buildRightsPayload = (rightsProvider: CityConfigFormValues["rightsProvider"]): RightsProvider | null => {
  return rightsProvider?.provider ? (rightsProvider as RightsProvider) : null;
};

export const CityConfigModal = ({ cityId, onClose }: CityConfigModalProps) => {
  const { data, isLoading: isLoadingConfig, error: loadError } = useGetCityConfig(cityId);
  const { updateCityConfig, isLoading: isSaving } = useUpdateCityConfig(cityId);

  const form = useForm<CityConfigFormValues>({
    resolver: zodResolver(cityConfigSchema),
    defaultValues: {
      ticketProvider: { passwordAlreadySet: false },
      rightsProvider: {},
    },
  });

  const { control, handleSubmit, setValue, reset } = form;

  useEffect(() => {
    if (data?.ticketProvider || data?.rightsProvider) {
      reset({
        ticketProvider: {
          ...withArrayDefaults(data.ticketProvider, TICKET_PROVIDER_ARRAY_FIELDS),
          passwordAlreadySet: data.ticketProvider?.provider === TicketProviderEnum.Extenso,
        },
        rightsProvider: withArrayDefaults(data.rightsProvider, RIGHTS_PROVIDER_ARRAY_FIELDS),
      });
    }
  }, [data, reset]);

  const save = async (values: CityConfigFormValues) => {
    try {
      await updateCityConfig({
        ticketProvider: buildTicketPayload(values.ticketProvider),
        rightsProvider: buildRightsPayload(values.rightsProvider),
      });
      showNotification({ variant: "success", message: "Configuration enregistrée" });
      onClose();
    } catch (error) {
      showNotification({ variant: "error", message: formatApiError(error, "Erreur lors de l'enregistrement") });
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90vh] flex-col p-0 sm:max-w-2xl">
        <Form {...form}>
          <form onSubmit={handleSubmit(save)} className="flex flex-col flex-1 min-h-0">
            <div className="px-6 pt-6 pb-4 border-b border-border">
              <DialogHeader>
                <DialogTitle>Configuration de la ville</DialogTitle>
                <DialogDescription>
                  Configurez les providers de la ville. Ces deux blocs sont optionnels.
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-hide px-6 py-6 space-y-6">
              {isLoadingConfig ? (
                <div className="text-sm text-muted-foreground text-center py-8">Chargement...</div>
              ) : loadError ? (
                <div className="text-sm text-destructive text-center py-8">
                  Impossible de charger la configuration. Veuillez réessayer.
                </div>
              ) : (
                <>
                  <SectionDivider label="Concentrateur de tickets" />
                  <TicketProviderSection control={control} setValue={setValue} />

                  <SectionDivider label="Service de gestion de droits" />
                  <RightsProviderSection control={control} setValue={setValue} />
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-border">
              <DialogFooter className="sm:justify-between">
                <Button variant="secondary" onClick={onClose} type="button" disabled={isSaving}>
                  Annuler
                </Button>
                <Button type="submit" disabled={isLoadingConfig || isSaving || !!loadError}>
                  {isSaving ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </DialogFooter>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
